import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/joy';
import {
  Add,
  AdminPanelSettings,
  EventNote,
  FolderOpen,
  Group,
  PendingActions,
  PersonAdd,
  Schedule,
  Security,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { dashboardService } from '@/services/dashboardService';
import type {
  AdminDashboardData,
  ActivityFeedItem,
  ProjectOverviewEntry,
} from '@/types/dashboard';
import WelcomeHeader from '@/components/dashboard/WelcomeHeader';
import { useDashboardRefresh } from '@/components/dashboard/useDashboardRefresh';
import StatCard from '@/components/dashboard/StatCard';
import WidgetPanel from '@/components/dashboard/WidgetPanel';
import ActivityItem from '@/components/dashboard/ActivityItem';
import ProjectCard from '@/components/dashboard/ProjectCard';
import NotificationWidget from '@/components/NotificationWidget';

const STATUS_COLORS: Record<string, string> = {
  draft: 'warning.400',
  published: 'success.400',
  updated: 'primary.400',
};

const CR_STATUS_COLORS: Record<string, 'warning' | 'primary' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning',
  under_review: 'primary',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
  expired: 'neutral',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const [projects, setProjects] = useState<ProjectOverviewEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashData, feed, projectsData] = await Promise.all([
        dashboardService.getAdminDashboard(),
        dashboardService.getActivityFeed(10),
        dashboardService.getProjectsOverview(10),
      ]);
      setData(dashData);
      setActivityFeed(feed);
      setProjects(projectsData);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDashboardRefresh(fetchData);

  const totalSchedules = data?.scheduleHealth.by_status.reduce((sum, s) => sum + s.count, 0) || 0;
  const totalCR = data?.changeRequestPipeline.by_status.reduce((sum, s) => sum + s.count, 0) || 0;

  return (
    <Box>
      <WelcomeHeader onRefresh={fetchData} />

      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 1.5 }}>Quick Actions</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startDecorator={<PersonAdd />} onClick={() => navigate('/users')}>
              Add User
            </Button>
            <Button variant="outlined" startDecorator={<Schedule />} onClick={() => navigate('/schedule')}>
              Add Schedule
            </Button>
            <Button variant="outlined" startDecorator={<Add />} onClick={() => navigate('/projects')}>
              Create Project
            </Button>
            <Button variant="outlined" startDecorator={<Security />} onClick={() => navigate('/admin/permissions')}>
              Manage Permissions
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* System Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<Group sx={{ color: 'white' }} />}
            value={data?.systemStats.total_users ?? '-'}
            label="Total Users"
            color="primary.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<EventNote sx={{ color: 'white' }} />}
            value={data?.systemStats.active_schedules_week ?? '-'}
            label="Active Schedules (Week)"
            color="success.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<PendingActions sx={{ color: 'white' }} />}
            value={data?.systemStats.pending_change_requests ?? '-'}
            label="Pending Requests"
            color="warning.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<FolderOpen sx={{ color: 'white' }} />}
            value={data?.systemStats.open_projects ?? '-'}
            label="Open Projects"
            color="info.500"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid xs={12} md={6}>
          <Stack spacing={3}>
            {/* User Distribution */}
            <WidgetPanel
              title="User Distribution"
              loading={loading}
              isEmpty={!data?.userDistribution}
              onViewAll={() => navigate('/users')}
              viewAllLabel="Manage Users"
            >
              {data?.userDistribution && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {data.userDistribution.by_role.map(r => (
                      <Card key={r.role} variant="outlined" sx={{ flex: '1 1 100px', minWidth: 100 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                          <Typography level="h4">{r.count}</Typography>
                          <Typography level="body-xs" color="neutral" textTransform="capitalize">
                            {r.role}s
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography level="body-sm">
                      Active: <strong>{data.userDistribution.active_count}</strong>
                    </Typography>
                    <Typography level="body-sm">
                      Inactive: <strong>{data.userDistribution.inactive_count}</strong>
                    </Typography>
                  </Stack>
                  {data.userDistribution.recent_users.length > 0 && (
                    <Box>
                      <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>
                        Recently created (last 7 days)
                      </Typography>
                      {data.userDistribution.recent_users.slice(0, 3).map(u => (
                        <Typography key={u.id} level="body-xs">
                          {u.first_name} {u.last_name}{' '}
                          <Chip size="sm" variant="soft" sx={{ '--Chip-minHeight': '16px', fontSize: '0.65rem' }}>
                            {u.role}
                          </Chip>
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Stack>
              )}
            </WidgetPanel>

            {/* Schedule Health */}
            <WidgetPanel
              title="Schedule Health"
              loading={loading}
              isEmpty={!data?.scheduleHealth}
              onViewAll={() => navigate('/schedule')}
              viewAllLabel="View Schedules"
            >
              {data?.scheduleHealth && (
                <Stack spacing={2}>
                  {data.scheduleHealth.by_status.map(s => {
                    const pct = totalSchedules > 0 ? Math.round((s.count / totalSchedules) * 100) : 0;
                    return (
                      <Box key={s.status}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm" textTransform="capitalize">
                            {s.status}
                          </Typography>
                          <Typography level="body-sm">
                            {s.count} ({pct}%)
                          </Typography>
                        </Stack>
                        <LinearProgress
                          determinate
                          value={pct}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'neutral.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: STATUS_COLORS[s.status] || 'neutral.400',
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                  {data.scheduleHealth.unassigned_shifts_next_week > 0 && (
                    <Chip size="sm" color="warning" variant="soft">
                      {data.scheduleHealth.unassigned_shifts_next_week} draft shifts in the next 7 days
                    </Chip>
                  )}
                </Stack>
              )}
            </WidgetPanel>

            {/* Change Request Pipeline */}
            <WidgetPanel
              title="Change Request Pipeline"
              loading={loading}
              isEmpty={!data?.changeRequestPipeline}
              onViewAll={() => navigate('/change-requests')}
              viewAllLabel="View All"
            >
              {data?.changeRequestPipeline && (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {data.changeRequestPipeline.by_status.map(s => (
                      <Chip
                        key={s.status}
                        size="sm"
                        variant="soft"
                        color={CR_STATUS_COLORS[s.status] || 'neutral'}
                      >
                        {s.status}: {s.count}
                      </Chip>
                    ))}
                  </Stack>
                  {data.changeRequestPipeline.aging_count > 0 && (
                    <Card variant="outlined" color="warning" sx={{ p: 1.5 }}>
                      <Typography level="body-sm" color="warning">
                        {data.changeRequestPipeline.aging_count} request(s) pending for more than 48 hours
                      </Typography>
                    </Card>
                  )}
                </Stack>
              )}
            </WidgetPanel>
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid xs={12} md={6}>
          <Stack spacing={3}>
            {/* Active Projects */}
            <WidgetPanel
              title="Active Projects"
              loading={loading}
              isEmpty={!projects.length}
              emptyMessage="No active projects. Create one to get started!"
              onViewAll={() => navigate('/projects')}
              viewAllLabel="View All Projects"
            >
              <Box>
                {projects.map(project => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </Box>
            </WidgetPanel>

            {/* Activity Feed */}
            <WidgetPanel
              title="Recent Activity"
              loading={loading}
              isEmpty={!activityFeed.length}
              emptyMessage="No recent activity"
            >
              <Stack spacing={0} divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
                {activityFeed.map((item, idx) => (
                  <ActivityItem key={idx} item={item} />
                ))}
              </Stack>
            </WidgetPanel>

            {/* Notifications */}
            <NotificationWidget maxItems={5} />
          </Stack>
        </Grid>
      </Grid>

    </Box>
  );
}
